import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "next-i18next";
import DivisionCinema from "../../../../components/LandingPage/BusinessDivision/DivisionCinema";
import ParticleSculpture from "../../../../components/LandingPage/BusinessDivision/ParticleSculpture";
import { PillButton } from "../../../../components/LandingPage/v2/primitives";

/**
 * Full-viewport arrival scene: the same cinema engine that stages the unit on
 * the home keeps playing here, so landing via the warp transition reads as one
 * continuous shot — the sculpture the visitor clicked is waiting on stage.
 * The old banner's word strip survives as an outlined marquee at the base.
 */
const Hero = ({ division, theme, selectedLanguage }) => {
	const { t } = useTranslation();
	const cinemaRef = useRef(null);
	const discRef = useRef(null);

	// the cinema engine orbits its effects around the sculpture disc
	useEffect(() => {
		const api = cinemaRef.current;
		if (!api) return undefined;
		const measure = () => {
			const disc = discRef.current;
			const rect = api.getRect();
			if (!disc || !rect) return;
			const d = disc.getBoundingClientRect();
			api.setAnchor(
				d.left + d.width / 2 - rect.left,
				d.top + d.height / 2 - rect.top,
				disc.offsetWidth / 2,
			);
		};
		const raf = requestAnimationFrame(measure);
		window.addEventListener("resize", measure);
		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener("resize", measure);
		};
	}, []);

	const name = t(`businessDivision.${division.id}`);
	const words = division.bannerText[selectedLanguage]
		.split(".")
		.map((w) => w.trim())
		.filter(Boolean);

	return (
		<Scene $accent={theme.accent}>
			<DivisionCinema
				ref={cinemaRef}
				initialMode={theme.mode}
				initialAccent={theme.accent}
			/>
			<Inner>
				<Copy $accent={theme.accent}>
					<span className="overline">
						{t("businessDivisionV2.chapter")} · {name}
					</span>
					<h1>{name}</h1>
					<p className="lead">{t(`businessDivision.${division.id}Title`)}</p>
					<p className="body">{t(`businessDivision.${division.id}Text`)}</p>
					<div className="actions">
						<PillButton as="a" href="#contact-us" $accent={theme.accent}>
							{t("businessDivisionV2.heroCta")}
							<svg
								width="15"
								height="15"
								viewBox="0 0 16 16"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M3 8h10M9 4l4 4-4 4"
									stroke="currentColor"
									strokeWidth="1.6"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</PillButton>
					</div>
				</Copy>

				<Stage $accent={theme.accent}>
					<div className="disc" ref={discRef}>
						<span className="halo" aria-hidden="true" />
						<ParticleSculpture shape={theme.sculpture} accent={theme.accent} />
					</div>
				</Stage>
			</Inner>

			<Marquee $accent={theme.accent} aria-hidden="true">
				<div className="rail">
					{[0, 1].map((copy) => (
						<span className="set" key={copy}>
							{words.map((w) => (
								<span className="word" key={`${copy}-${w}`}>
									{w}
									<i className="dot" />
								</span>
							))}
						</span>
					))}
				</div>
			</Marquee>

			<ScrollHint $accent={theme.accent}>
				<span>{t("businessDivisionV2.heroScroll")}</span>
				<i aria-hidden="true" />
			</ScrollHint>
		</Scene>
	);
};

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(26px);
    filter: blur(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const discIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.84);
    filter: blur(10px);
  }
  to {
    opacity: 1;
    transform: scale(1);
    filter: blur(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
`;

const slide = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const hintPulse = keyframes`
  0% { transform: scaleY(0); transform-origin: top; }
  45% { transform: scaleY(1); transform-origin: top; }
  55% { transform: scaleY(1); transform-origin: bottom; }
  100% { transform: scaleY(0); transform-origin: bottom; }
`;

const Scene = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100svh;
  background: #000104;
  overflow: hidden;
  padding: 130px 24px 120px;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(
      ellipse 60% 48% at 72% 30%,
      ${(p) => p.$accent + "17"} 0%,
      transparent 70%
    );
  }

  @media (min-width: 992px) {
    padding-left: 64px;
    padding-right: 64px;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1216px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 26px;
  align-items: center;

  @media (min-width: 992px) {
    grid-template-columns: 1.1fr 1fr;
    gap: 48px;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  > * {
    animation: ${rise} 0.85s cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .overline {
    animation-delay: 0.15s;
    display: inline-flex;
    align-items: center;
    gap: 14px;
    font-family: "Hind Madurai";
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: ${(p) => p.$accent};
    margin-bottom: 18px;
    /* Tailwind's global ".overline" utility strikes again */
    text-decoration: none;

    &::before {
      content: "";
      height: 1px;
      width: 38px;
      background: linear-gradient(90deg, transparent, ${(p) => p.$accent + "aa"});
    }
  }

  h1 {
    animation-delay: 0.25s;
    font-family: "Hind Madurai";
    font-weight: 600;
    font-size: clamp(3rem, 7.5vw, 5.4rem);
    line-height: 1.04;
    letter-spacing: -1.5px;
    text-transform: capitalize;
    color: ${(p) => p.$accent};
    text-shadow: 0 0 44px ${(p) => p.$accent + "66"};
    margin: 0 0 18px 0;
  }

  .lead {
    animation-delay: 0.35s;
    font-family: "Hind Madurai";
    font-size: clamp(1.1rem, 1.8vw, 1.45rem);
    font-weight: 600;
    line-height: 1.45;
    color: #ffffff;
    max-width: 520px;
    margin: 0 0 14px 0;
    text-wrap: balance;
  }

  .body {
    animation-delay: 0.45s;
    font-family: "Hind Madurai";
    font-size: clamp(0.95rem, 1.2vw, 1.05rem);
    line-height: 1.75;
    color: #a9afb8;
    max-width: 540px;
    margin: 0 0 32px 0;
  }

  .actions {
    animation-delay: 0.55s;
    display: flex;
    gap: 16px;

    a {
      text-decoration: none;
    }
  }

  @media (max-width: 991px) {
    align-items: center;
    text-align: center;
    order: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    > * {
      animation: none;
    }
  }
`;

const Stage = styled.div`
  position: relative;

  .disc {
    position: relative;
    width: min(72vw, 460px);
    aspect-ratio: 1;
    margin: 0 auto;
    animation:
      ${discIn} 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both,
      ${float} 5.5s ease-in-out 1.4s infinite;
  }

  .halo {
    position: absolute;
    inset: -20%;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${(p) => p.$accent + "3d"} 0%,
      ${(p) => p.$accent + "14"} 46%,
      transparent 70%
    );
    filter: blur(4px);
  }

  @media (max-width: 991px) {
    order: 0;

    .disc {
      width: min(58vw, 300px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .disc {
      animation: none;
    }
  }
`;

const Marquee = styled.div`
  position: relative;
  z-index: 1;
  margin-top: clamp(40px, 7vh, 90px);
  overflow: hidden;
  animation: ${rise} 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.65s both;
  mask-image: linear-gradient(
    90deg,
    transparent 0%,
    #000 12%,
    #000 88%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent 0%,
    #000 12%,
    #000 88%,
    transparent 100%
  );

  .rail {
    display: flex;
    width: max-content;
    animation: ${slide} 26s linear infinite;
  }

  .set {
    display: flex;
    flex-shrink: 0;
  }

  .word {
    display: inline-flex;
    align-items: center;
    font-family: "Hind Madurai";
    font-size: clamp(2rem, 4.6vw, 3.6rem);
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: transparent;
    -webkit-text-stroke: 1px ${(p) => p.$accent + "59"};
    white-space: nowrap;

    .dot {
      display: inline-block;
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: ${(p) => p.$accent};
      box-shadow: 0 0 14px ${(p) => p.$accent + "aa"};
      margin: 0 clamp(18px, 3vw, 40px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    .rail {
      animation: none;
    }
  }
`;

const ScrollHint = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: clamp(26px, 4vh, 48px);
  animation: ${rise} 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.8s both;

  span {
    font-family: "Hind Madurai";
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #5c6470;
  }

  i {
    display: block;
    width: 1px;
    height: 42px;
    background: linear-gradient(180deg, ${(p) => p.$accent}, transparent);
    animation: ${hintPulse} 2.2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    i {
      animation: none;
    }
  }
`;

export default Hero;
